import React, { useState, useRef, useEffect } from "react";
import { FaUndoAlt,FaRedoAlt } from "react-icons/fa";
import Backend from "../utils/Backend";
import Event from "../utils/Event";
import Notify from "../utils/Notify";
import General from "../utils/General";


export default function ActionToolbar(props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const [history, setHistory] = useState({
    loading: false,
    previous: [],
    next: [],
  });

  const {
    loading,
    previous,
    next
  } = history;

  // Check for toolbarColor=brand in URL
  const params = new URLSearchParams(window.location.search);
  const toolbarColor = params.get("toolbarColor");
  const colorClass = toolbarColor === "brand" ? "brand-bg" : "";

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    window.onChangeLog = (changeLog) => {
      setHistory((prev) => ({
        ...prev,
        previous: [...prev.previous, changeLog],
        next: []
      }));
    }
    loadChangeLogs()

    Event.on('CHANGE_LOGGING', () => {
      setHistory((prev) => ({
        ...prev,
        loading: true,
      }))
    });

    Event.on('CHANGE_LOGGED', () => {
      loadChangeLogs()
    });

    return () => {
      Event.off("CHANGE_LOGGED");
      Event.off("CHANGE_LOGGING");
    }
  }, [])

  useEffect(() => {
    function handleKeyDown(event) {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

      if ((isMac ? event.metaKey : event.ctrlKey) && event.key === "z" && !event.shiftKey) {
        event.preventDefault();
        if(!loading){
          undo();
        }
      }

      if ((isMac ? event.metaKey : event.ctrlKey) && event.key === "z" && event.shiftKey) {
        event.preventDefault();
        if(!loading){
          redo();
        }
      }

      if (!isMac && event.ctrlKey && event.key === "y") {
        event.preventDefault();
        if(!loading){
          redo();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const loadChangeLogs = () => {
    setHistory((prev) => ({
      ...prev,
      loading: true,
    }))
    Backend.getChangeLogs(props.website, props.page)
    .then(changeLogs => {
      setHistory({
        loading: false,
        previous: changeLogs.filter(changeLog => changeLog.undone === false),
        next: changeLogs.filter(changeLog => changeLog.undone === true)
      });
    })
  }

  const undo = () => {
    setHistory((prev) => ({ ...prev, loading: true }))
    Backend.undoPreviousChangeLog(props.page)
    .then((data) => {
      handleChangeLogResponse(data, false)
    })
  }

  const redo = () => {
    setHistory((prev) => ({ ...prev, loading: true }))
    Backend.redoNextChangeLog(props.page)
      .then((data) => {
        handleChangeLogResponse(data, true)
      })
  }

  const handleChangeLogResponse = (data, redo) => {
    const affectedChangeLogId = data.affected_change_log_id

    setHistory((prev) => {
      const changeLogs = [...prev.previous, ...prev.next]
      const changeLog = changeLogs.find(changeLog => changeLog.id === affectedChangeLogId)

      if(!changeLog) {
        Notify.error("Failed to reconcile change, reloading")
        setTimeout(() => {
          window.location.reload()
        })
        return prev;
      }

      const message = redo ? "Change Redone" : "Change Undone"

      if(changeLog.action === "reorder"){
        General.showNotification(message);
        Event.emit('CONTENT_BLOCKS_REORDERED', { data })
      }
      else if(changeLog.menu){
        General.showNotification(message);
        Event.emit('MENU_UPDATED', data)
      }
      else if(changeLog.footer_menu){
        const footer = document.querySelector( 'footer' );
        if(footer){
          footer.scrollIntoView( { behavior: 'smooth', block: 'start' } );
        }
        setTimeout(() => {
          General.showNotification(message);
          Event.emit('FOOTER_UPDATED', data)
        }, 500)
      }
      else if(changeLog.website){
        General.showNotification(message);
        window.CURRENT_WEBSITE = data
        Event.emit('WEBSITE_CHANGE_UPDATED', data)
      }
      else if(changeLog.page){
        General.showNotification(message);
        Event.emit('PAGE_UPDATED', data)
      }
      else if(changeLog.content_block){
        General.showNotification(message);
        Event.emit('CONTENT_BLOCK_UPDATED', { data, changeLog, redo })
      }
      else {
        Notify.error("Failed to reconcile change, reloading")
        setTimeout(() => {
          window.location.reload()
        })
        return prev;
      }

      if(redo){
        return {
          loading: false,
          previous: [changeLog, ...prev.previous],
          next: next.filter(log => log.id !== affectedChangeLogId)
        }
      } else {
        return {
          loading: false,
          previous: previous.filter(log => log.id !== affectedChangeLogId),
          next: [changeLog, ...prev.next]
        }
      }
    })
  }

  if(loading) {
    return null
  }

  return (
    <div
      ref={menuRef}
      className={`action-toolbar ${props.variant ? props.variant : ""} ${colorClass}`}
    >
      <button
        className="at-toggle"
        onClick={() => setMenuOpen((prev) => !prev)}
      >
        {menuOpen ? (
          <i className="material-icons">close</i>
        ) : (
          <i className="material-icons">more_horiz</i>
        )}
      </button>
      {menuOpen && (
        <div
          className="at-menu"
        >
          { previous.length > 0 && <ToolbarItem icon={<FaUndoAlt />} label="Undo" onClick={undo} disabled={loading} /> }
          { next.length > 0 && <ToolbarItem icon={<FaRedoAlt />} label="Redo" onClick={redo} disabled={loading} /> }
        </div>
      )}
    </div>
  );
}

function ToolbarItem({ icon, label, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
